import { Title } from '@solidjs/meta'
import { type Component, For } from 'solid-js'
import Alert from '~/components/Alert'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import { controlSizes } from '~/utils/controlSize'

// ── Datos ──────────────────────────────────────────────────────────────────

/** Escala de Tailwind. `clase` va literal: Tailwind lee el fuente como texto. */
const TAMANOS = [
  { clase: 'text-xs', medida: '12 px', interlineado: '16 px', uso: 'Metadatos, contadores y el mensaje de un campo pequeño' },
  { clase: 'text-sm', medida: '14 px', interlineado: '20 px', uso: 'El tamaño de trabajo: tablas, formularios y botones' },
  { clase: 'text-base', medida: '16 px', interlineado: '24 px', uso: 'Texto corrido de una página pública' },
  { clase: 'text-lg', medida: '18 px', interlineado: '28 px', uso: 'Entradillas y controles de talla `lg`' },
  { clase: 'text-xl', medida: '20 px', interlineado: '28 px', uso: 'Subtítulo de una pantalla' },
  { clase: 'text-2xl', medida: '24 px', interlineado: '32 px', uso: 'Título de una tarjeta o de un modal' },
  { clase: 'text-3xl', medida: '30 px', interlineado: '36 px', uso: 'Cifra destacada de un panel' },
  { clase: 'text-4xl', medida: '36 px', interlineado: '40 px', uso: 'Título de pantalla' },
  { clase: 'text-5xl', medida: '48 px', interlineado: '1', uso: 'Titular de una portada' }
] as const

const PESOS = [
  { clase: 'font-light', valor: '300', uso: 'Texto de ayuda bajo un campo' },
  { clase: 'font-normal', valor: '400', uso: 'Todo el texto corrido' },
  { clase: 'font-medium', valor: '500', uso: 'Etiquetas y la opción elegida de una lista' },
  { clase: 'font-semibold', valor: '600', uso: 'Títulos de tarjeta y cabeceras de tabla' },
  { clase: 'font-bold', valor: '700', uso: 'Títulos de pantalla' },
  { clase: 'font-black', valor: '900', uso: 'Titulares de portada y la marca' }
] as const

const INTERLINEADOS = [
  { clase: 'leading-none', valor: '1', uso: 'Sólo para una línea suelta que no crece: una cifra, un contador' },
  { clase: 'leading-tight', valor: '1.25', uso: 'Titulares y el mensaje de error de un campo' },
  { clase: 'leading-normal', valor: '1.5', uso: 'Texto corrido' },
  { clase: 'leading-relaxed', valor: '1.625', uso: 'Párrafos largos de una página pública' }
] as const

const TALLAS = ['xs', 'sm', 'md', 'lg', 'xl'] as const

// ── Piezas de la página ────────────────────────────────────────────────────

const FilaEjemplo: Component<{ clase: string; detalle: string; uso: string; children: string }> = props => (
  <div class="flex flex-col gap-2 border-border border-b py-4 last:border-b-0 md:flex-row md:items-baseline md:gap-6">
    <div class="w-full shrink-0 md:w-56">
      <code class="block font-mono text-foreground text-sm">{props.clase}</code>
      <span class="block text-foreground-subtle text-xs">{props.detalle}</span>
    </div>
    <div class="min-w-0 flex-1">
      <p class={`truncate text-foreground ${props.clase}`}>{props.children}</p>
      <span class="text-foreground-muted text-sm">{props.uso}</span>
    </div>
  </div>
)

const TypographyPage: Component = () => (
  <>
    <Title>Tipografía - Florez Jaramillo Solutions</Title>

    <Container class="py-8">
      <div class="mt-6 mb-10">
        <h1 class="mb-4 font-bold text-4xl text-foreground">Tipografía</h1>
        <p class="max-w-3xl text-foreground-subtle text-xl leading-relaxed">
          La jerarquía se construye con tres palancas —tamaño, peso y color— y casi siempre basta con mover una. Bajar de <code>text-foreground</code> a <code>text-foreground-muted</code> separa dos
          niveles sin tocar el tamaño.
        </p>
      </div>

      <div class="space-y-12">
        {/* ── Familia ──────────────────────────────────────────────────── */}
        <Card>
          <Card.Header
            title="La familia"
            subtitle="La del sistema operativo, sin fuente descargada."
            icon={<Icon icon="material-symbols-light:font-download-outline" class="text-primary-500" size={24} />}
          />
          <Card.Body>
            <div class="rounded-xl border border-border bg-surface-muted p-6">
              <p class="mb-2 font-mono text-foreground-subtle text-xs">system-ui, -apple-system, sans-serif</p>
              <p class="text-3xl text-foreground">Aa Bb Cc Ññ 0123456789</p>
              <p class="text-foreground-muted">El zorro veloz saltó sobre el perro perezoso · ¿Cuánto cuesta? ¡Qué bien!</p>
            </div>
            <p class="mt-4 text-foreground-muted text-sm">
              No se carga ninguna fuente remota: cada sistema pone la suya, así que el texto se ve nativo y aparece sin esperas ni saltos. Es también lo que permite que la aplicación funcione sin
              salida a internet.
            </p>
          </Card.Body>
        </Card>

        {/* ── Tamaños ──────────────────────────────────────────────────── */}
        <Card>
          <Card.Header
            title="Tamaños"
            subtitle="La escala completa, con el sitio donde encaja cada paso."
            icon={<Icon icon="material-symbols-light:format-size" class="text-primary-500" size={24} />}
          />
          <Card.Body>
            <For each={TAMANOS}>
              {tamano => (
                <FilaEjemplo clase={tamano.clase} detalle={`${tamano.medida} · interlineado ${tamano.interlineado}`} uso={tamano.uso}>
                  El zorro veloz saltó sobre el perro perezoso
                </FilaEjemplo>
              )}
            </For>
          </Card.Body>
        </Card>

        {/* ── Pesos ────────────────────────────────────────────────────── */}
        <Card>
          <Card.Header
            title="Pesos"
            subtitle="Subir de peso destaca; subir de tamaño reordena. No son intercambiables."
            icon={<Icon icon="material-symbols-light:format-bold" class="text-primary-500" size={24} />}
          />
          <Card.Body>
            <For each={PESOS}>
              {peso => (
                <FilaEjemplo clase={peso.clase} detalle={peso.valor} uso={peso.uso}>
                  El zorro veloz saltó sobre el perro perezoso
                </FilaEjemplo>
              )}
            </For>
          </Card.Body>
        </Card>

        {/* ── Interlineado ─────────────────────────────────────────────── */}
        <Card>
          <Card.Header title="Interlineado" subtitle="Cuánto respira cada línea." icon={<Icon icon="material-symbols-light:format-line-spacing" class="text-primary-500" size={24} />} />
          <Card.Body>
            <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
              <For each={INTERLINEADOS}>
                {espaciado => (
                  <div class="rounded-xl border border-border p-5">
                    <div class="mb-3 flex items-baseline justify-between gap-2">
                      <code class="font-mono text-foreground text-sm">{espaciado.clase}</code>
                      <span class="font-mono text-foreground-subtle text-xs">{espaciado.valor}</span>
                    </div>
                    <p class={`text-foreground text-sm ${espaciado.clase}`}>
                      Los totales se recalculan siempre en el backend sobre las líneas vigentes, porque el cliente puede llegar tarde y con datos viejos.
                    </p>
                    <span class="mt-3 block text-foreground-muted text-sm">{espaciado.uso}</span>
                  </div>
                )}
              </For>
            </div>
            <Alert variant="warning" title="Cuidado con leading-none" class="mt-6">
              <p class="text-sm">
                Con <code>text-sm</code> recorta el trazo inferior de la <em>g</em>, la <em>p</em> y la <em>j</em>. Los mensajes de los campos usan <code>leading-tight</code> justo por eso.
              </p>
            </Alert>
          </Card.Body>
        </Card>

        {/* ── Jerarquía ────────────────────────────────────────────────── */}
        <Card>
          <Card.Header
            title="Jerarquía de una pantalla"
            subtitle="Cómo se combinan las tres palancas en el panel."
            icon={<Icon icon="material-symbols-light:format-h1" class="text-primary-500" size={24} />}
          />
          <Card.Body>
            <div class="space-y-6 rounded-xl border border-border bg-surface-muted p-6">
              <div>
                <h2 class="mb-1 font-bold text-3xl text-foreground">Título de la pantalla</h2>
                <code class="font-mono text-foreground-subtle text-xs">text-3xl font-bold text-foreground</code>
                <p class="mt-1 text-foreground-subtle">Descripción de lo que se hace aquí</p>
                <code class="font-mono text-foreground-subtle text-xs">text-base text-foreground-subtle</code>
              </div>

              <div class="border-border border-t pt-5">
                <h3 class="mb-1 font-semibold text-foreground text-xl">Título de una tarjeta</h3>
                <code class="font-mono text-foreground-subtle text-xs">text-xl font-semibold text-foreground</code>
                <p class="mt-1 text-foreground-muted text-sm">Subtítulo que explica qué contiene</p>
                <code class="font-mono text-foreground-subtle text-xs">text-sm text-foreground-muted</code>
              </div>

              <div class="border-border border-t pt-5">
                <span class="mb-1 block font-medium text-foreground text-sm">Etiqueta de un campo</span>
                <code class="font-mono text-foreground-subtle text-xs">text-sm font-medium text-foreground</code>
                <p class="mt-1 font-light text-foreground-subtle text-sm">Texto de ayuda bajo el campo</p>
                <code class="font-mono text-foreground-subtle text-xs">text-sm font-light text-foreground-subtle</code>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* ── Controles ────────────────────────────────────────────────── */}
        <Card>
          <Card.Header
            title="El texto de los controles"
            subtitle="No se elige campo a campo: lo fija la talla, y sale de una sola escala compartida."
            icon={<Icon icon="material-symbols-light:tune" class="text-primary-500" size={24} />}
          />
          <Card.Body>
            <p class="mb-5 text-foreground-muted">
              Un <code>size</code> decide a la vez la altura del control, su texto, el de su etiqueta flotante y el de su mensaje de error. Está todo en <code>src/utils/controlSize.ts</code>: si hay
              que tocar una talla, se toca ahí y cambian los campos y los botones a la vez.
            </p>
            <div class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-border border-b text-left">
                    <th class="py-2 pe-4 font-semibold text-foreground">Talla</th>
                    <th class="py-2 pe-4 font-semibold text-foreground">Alto</th>
                    <th class="py-2 pe-4 font-semibold text-foreground">Texto</th>
                    <th class="py-2 pe-4 font-semibold text-foreground">Etiqueta</th>
                    <th class="py-2 font-semibold text-foreground">Mensaje</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={TALLAS}>
                    {talla => (
                      <tr class="border-border border-b last:border-b-0">
                        <td class="py-2 pe-4 font-mono text-foreground">{talla}</td>
                        <td class="py-2 pe-4 font-mono text-foreground-muted">{controlSizes[talla].height}</td>
                        <td class="py-2 pe-4 font-mono text-foreground-muted">{controlSizes[talla].text}</td>
                        <td class="py-2 pe-4 font-mono text-foreground-muted">{controlSizes[talla].labelFloating}</td>
                        <td class="py-2 font-mono text-foreground-muted">{controlSizes[talla].messageText}</td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>

        {/* ── Contenido del editor ─────────────────────────────────────── */}
        <Card>
          <Card.Header
            title="Contenido escrito por el usuario"
            subtitle="Lo que sale del EditorInput se pinta con `prose`."
            icon={<Icon icon="material-symbols-light:article-outline" class="text-primary-500" size={24} />}
          />
          <Card.Body>
            <p class="mb-5 text-foreground-muted">
              La descripción larga de un producto o el cuerpo de una página del constructor llegan como HTML. Sin la clase <code>prose</code> no se les aplica ningún estilo y un <code>h2</code> se ve
              igual que un párrafo.
            </p>
            <div class="prose max-w-none rounded-xl border border-border bg-surface-muted p-6">
              <h2>Un título dentro del contenido</h2>
              <p>
                Un párrafo con <strong>negrita</strong>, <em>cursiva</em> y un <a href="#contenido">enlace</a>, para ver cómo quedan juntos.
              </p>
              <ul>
                <li>Primer punto de una lista</li>
                <li>Segundo punto</li>
              </ul>
            </div>
          </Card.Body>
        </Card>
      </div>
    </Container>
  </>
)

export default TypographyPage
